import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Phone, Check, Users, Droplets, Zap, Ruler, ArrowRight, Info, X, GitCompare, Download, Smartphone } from 'lucide-react';
import { getProductById, getRelatedModel, VIKING_SPAS_PRODUCTS, DYNASTY_SPAS_PRODUCTS, VS_SHELL_COLORS, VS_ELITE_CABINET_COLORS, VS_HEIRLOOM_CABINET_COLORS, DYNASTY_SHELL_COLORS, DYNASTY_CABINET_COLORS, GRAND_RIVER_EXTRAS, SAUNA_INSTALLATION_OPTION, DYNASTY_LUXURY_EXTRAS, DYNASTY_OASIS_EXTRAS, DYNASTY_VACATION_EXTRAS, DYNASTY_HIDEAWAY_EXTRAS, DYNASTY_FAMILY_EXTRAS, DYNASTY_AQUEX_EXTRAS, VIKING_SPAS_EXTRAS, VIKING_SPAS_FREE_ITEMS, AQUORA_ALL_SEASON_FEATURES, AQUORA_HEAT_FEATURES, AQUORA_COMPLETE_FEATURES, AQUORA_CORE_FEATURES, AQUORA_EXTRAS } from '../data/products';
import { ASSETS, CONTACT } from '../data/constants';

// Base URL for Grand River Spas visualizer images
const GR_VISUALIZER_BASE = 'https://grandriverspas.com/wp-content/plugins/spa-visualizer/assets/dist/img';
// Base URL for Viking Spas visualizer images
const VS_VISUALIZER_BASE = 'https://vikingspas.com/wp-content/plugins/spa-visualizer/assets/dist/img';

// Shell and Cabinet colors for display
const SHELL_COLORS = {
  white: { name: 'White Satin', hex: '#F5F5F0', image: `${VS_VISUALIZER_BASE}/white.png` },
  silver: { name: 'Silver Satin', hex: '#C0C0C0', image: `${VS_VISUALIZER_BASE}/silver.png` },
  opal: { name: 'Opal Satin', hex: '#A8B5B8', image: `${VS_VISUALIZER_BASE}/opal.png` },
};

const CABINET_COLORS = {
  // Grand River
  coastalGray: { name: 'Coastal Gray', hex: '#6B7280', image: `${GR_VISUALIZER_BASE}/coastalgray.png` },
  walnut: { name: 'Walnut', hex: '#5D4037', image: `${VS_VISUALIZER_BASE}/walnut.png` },
  barnwood: { name: 'Barnwood', hex: '#8B7355', image: `${VS_VISUALIZER_BASE}/barnwood.png` },
  black: { name: 'Black Slate', hex: '#1a1a1a', image: `${VS_VISUALIZER_BASE}/black.png` },
  taupe: { name: 'Taupe', hex: '#8B7D6B', image: `${GR_VISUALIZER_BASE}/walnut.png` },
  // Viking Elite Series
  slate: { name: 'Slate', hex: '#708090', image: `${VS_VISUALIZER_BASE}/slate.png` },
  chestnut: { name: 'Chestnut', hex: '#954535', image: `${VS_VISUALIZER_BASE}/chestnut.png` },
  stone: { name: 'Stone', hex: '#8B8B83', image: `${VS_VISUALIZER_BASE}/stone.png` },
  carbon: { name: 'Carbon', hex: '#333333', image: `${VS_VISUALIZER_BASE}/carbon.png` },
  // Viking Heirloom Series
  ashGray: { name: 'Ash Gray', hex: '#B2BEB5', image: `${VS_VISUALIZER_BASE}/ash.png` },
  // Dynasty Spas Cabinet Colors
  blackConfer: { name: 'Black Confer', hex: '#1a1a1a' },
  grayConfer: { name: 'Gray Confer', hex: '#6B7280' },
  ashElite: { name: 'Ash Elite', hex: '#B2BEB5' },
  coastalGrayElite: { name: 'Coastal Gray Elite', hex: '#708090' },
};

// Unified color lookup - checks brand-specific colors first (with images), then generic
const getShellColorData = (colorKey) => DYNASTY_SHELL_COLORS[colorKey] || SHELL_COLORS[colorKey] || null;
const getCabinetColorData = (colorKey) => DYNASTY_CABINET_COLORS[colorKey] || CABINET_COLORS[colorKey] || null;

// Corner color options for Viking Spas
const CORNER_COLORS = {
  // Elite Series corners
  carbon: { name: 'Carbon', hex: '#333333', image: `${VS_VISUALIZER_BASE}/carbon.png` },
  // Heirloom Series corners
  black: { name: 'Black Slate', hex: '#1a1a1a', image: `${VS_VISUALIZER_BASE}/black.png` },
};

// Generate the actual color combination image URL for Grand River
const getGRColorComboImageUrl = (product, shellColor, cabinetColor, cornerOption) => {
  if (!product || product.brand !== 'Grand River Spas') {
    return null;
  }
  
  const modelName = product.modelFamily || product.name.split(' ')[0];
  
  const shellMap = { white: 'White', silver: 'Silver', opal: 'Opal' };
  const cabinetMap = { coastalGray: 'CoastalGray', walnut: 'Walnut', barnwood: 'Barnwood', black: 'Black', taupe: 'Taupe' };
  
  const shell = shellMap[shellColor] || 'White';
  const cabinet = cabinetMap[cabinetColor] || 'CoastalGray';
  const corner = cornerOption === 'black' ? 'Black' : cabinet;
  
  return `${GR_VISUALIZER_BASE}/${modelName}_${shell}_${cabinet}_${corner}.jpg`;
};

// Generate the actual color combination image URL for Viking Spas
const getVSColorComboImageUrl = (product, shellColor, cabinetColor, cornerOption) => {
  if (!product || product.brand !== 'Viking Spas') {
    return null;
  }
  
  // Use the colorVisualizerBase if available, otherwise derive from name
  const modelBase = product.colorVisualizerBase || product.modelFamily || product.name;
  
  const shellMap = { white: 'White', silver: 'Silver', opal: 'Opal' };
  
  // Elite Series cabinet map
  const eliteCabinetMap = { slate: 'Slate', chestnut: 'Chestnut', stone: 'Stone', carbon: 'Carbon' };
  // Heirloom Series cabinet map  
  const heirloomCabinetMap = { ashGray: 'Ash', walnut: 'Walnut', barnwood: 'Barnwood', black: 'Black' };
  // Element Series cabinet map
  const elementCabinetMap = { walnut: 'Walnut', black: 'Black', barnwood: 'Barnwood', coastalGray: 'CoastalGray' };
  
  const isElite = product.series === 'Elite Series';
  const isElement = product.series === 'Element Series';
  const cabinetMap = isElite ? eliteCabinetMap : isElement ? elementCabinetMap : heirloomCabinetMap;
  
  const shell = shellMap[shellColor] || 'Opal';
  const cabinet = cabinetMap[cabinetColor] || (isElite ? 'Carbon' : isElement ? 'Walnut' : 'Ash');
  
  // Corner: 'match' means same as cabinet, otherwise use Carbon (Elite) or Black (Heirloom/Element)
  let corner;
  if (cornerOption === 'match') {
    corner = cabinet;
  } else {
    corner = isElite ? 'Carbon' : 'Black';
  }
  
  return `${VS_VISUALIZER_BASE}/${modelBase}_${shell}_${cabinet}_${corner}.png`;
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const product = getProductById(id);
  const relatedModel = useMemo(() => getRelatedModel(product), [product]);
  
  // Brochure mapping for Dynasty Spas products
  const getBrochureUrl = (productName) => {
    const brochureMap = {
      'Paradise Bay': '/brochures/PARADISE_BAY_PRODUCT_SHEET.pdf',
      'Palm Island': '/brochures/PALM_ISLAND_PRODUCT_SHEET.pdf',
      'Pleasure Cove': '/brochures/PLEASURE_COVE_PRODUCT_SHEET.pdf',
      'Coconut Bay': '/brochures/COCONUT_BAY_PRODUCT_SHEET.pdf',
      'Caribbean Breeze': '/brochures/CARIBBEAN_BREEZE_PRODUCT_SHEET.pdf',
      'Nassau Royale': '/brochures/NASSAU_ROYALE_PRODUCT_SHEET.pdf',
      'Cabana Bay': '/brochures/CABANA_BAY_PRODUCT_SHEET.pdf',
      'Ocean Breeze': '/brochures/OCEAN_BREEZE_PRODUCT_SHEET.pdf',
      'Twin Palms': '/brochures/TWIN_PALMS_PRODUCT_SHEET-5ab8b7c0.pdf',
      'Bimini': '/brochures/BIMINI_2_PRODUCT_SHEET.pdf',
      'Treasure Cay': '/brochures/TREASURE_CAY_PRODUCT_SHEET.pdf',
      'Sunset Cove': '/brochures/SUNSET_COVE_PRODUCT_SHEET.pdf',
      'Tranquility Harbor': '/brochures/TRANQUILITY_HARBOR_PRODUCT_SHEET.pdf',
      'Serenity Cove': '/brochures/SERENITY_COVE_PRODUCT_SHEET.pdf',
      'Seaside': '/brochures/SEASIDE+PRODUCT+SHEET.pdf',
      'Bay Bliss': '/brochures/BAY+BLISS+PRODUCT+SHEET.pdf',
      'High Tide': '/brochures/HIGH+TIDE+PRODUCT+SHEET.pdf',
    };
    
    // Check each key to see if productName contains it
    for (const [key, url] of Object.entries(brochureMap)) {
      if (productName.toLowerCase().includes(key.toLowerCase())) {
        return url;
      }
    }
    // Default to full catalog
    return '/brochures/2025_DYNASTY_FULL_BROCHURE.pdf';
  };

  // Get brochure URL based on brand with page number for direct navigation
  const getProductBrochure = () => {
    if (!product) return null;
    
    // Check if product has a spec sheet (these are single-page PDFs, no page number needed)
    if (product.specSheet) {
      return product.specSheet;
    }
    
    // Dynasty Spas have individual product sheets (single page each)
    if (product.brand === 'Dynasty Spas') {
      return getBrochureUrl(product.name);
    }
    
    // Viking Spas brochure page mapping
    if (product.brand === 'Viking Spas') {
      const vikingBaseUrl = 'https://customer-assets.emergentagent.com/job_family-spa-luxury/artifacts/vmpqiwb2_Viking-Spas-Brochure_Rev-C_web.pdf';
      const vikingPageMap = {
        'Heritage 1': 15,
        'Heritage 2': 15,
        'Tradition 1': 17,
        'Tradition 2': 17,
        'Apex 1': 19,
        'Apex 2': 19,
        'Ascent 1': 21,
        'Ascent 2': 21,
        'Royale': 25,
        'Regal': 25,
        'Regal X': 25,
        'Aurora 3': 27,
        'Viking': 27,
        'Regal P': 31,
        'Viking P': 33,
        // Swim spas
        'Valhalla': 35,
        'Asgard': 37,
        'Odin': 39,
        'Thor': 41
      };
      const pageNum = vikingPageMap[product.name];
      return pageNum ? `${vikingBaseUrl}#page=${pageNum}` : vikingBaseUrl;
    }
    
    // Grand River Spas brochure page mapping
    if (product.brand === 'Grand River Spas') {
      const grBaseUrl = 'https://customer-assets.emergentagent.com/job_family-spa-luxury/artifacts/cdko0u59_GR-2025_Brochure_RevA_Update_E_Final_6.16_final_email.pdf';
      const grPageMap = {
        'Saginaw 1': 8,
        'Saginaw 2': 8,
        'Thornapple 1': 9,
        'Thornapple 2': 9,
        'Muskegon 1': 10,
        'Muskegon 2': 10,
        'Sturgeon 1': 11,
        'Sturgeon 2': 11,
        'Manistee 220v': 12,
        'Sturgeon Eco': 15,
        'Manistee Eco 120v': 15,
        // Swim spas
        'Wolverine 19': 17,
        'Wolverine 17': 17,
        'Wolverine 15': 17,
        'Wolverine 14': 17
      };
      const pageNum = grPageMap[product.name];
      return pageNum ? `${grBaseUrl}#page=${pageNum}` : grBaseUrl;
    }
    
    return null;
  };
  
  const productBrochure = getProductBrochure();
  
  const defaultShell = product?.shellColors?.[0] || 'white';
  const defaultCabinet = product?.cabinetColors?.[0] || 'coastalGray';
  
  const [selectedShell, setSelectedShell] = useState(defaultShell);
  const [selectedCabinet, setSelectedCabinet] = useState(defaultCabinet);
  // Corner option: 'match' means match cabinet color, or specific color like 'carbon' or 'black'
  const [selectedCorner, setSelectedCorner] = useState('match');
  const [currentView, setCurrentView] = useState('color');
  const [activeTab, setActiveTab] = useState('overview');
  const [imageError, setImageError] = useState(false);
  const [selectedCompareModel, setSelectedCompareModel] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  // Get gallery images for sauna/cold plunge products
  const galleryImages = useMemo(() => {
    if (!product?.images?.gallery || !Array.isArray(product.images.gallery)) return [];
    return [product.images.primary, ...product.images.gallery];
  }, [product?.images?.gallery, product?.images?.primary]);
  
  // Get comparable models from the same brand
  const comparableModels = useMemo(() => {
    if (!product) return [];
    
    if (product.brand === 'Viking Spas') {
      return VIKING_SPAS_PRODUCTS.filter(p => p.id !== product.id);
    }
    if (product.brand === 'Dynasty Spas') {
      return DYNASTY_SPAS_PRODUCTS.filter(p => p.id !== product.id);
    }
    return [];
  }, [product]);
  
  // Set document title - must be before any early returns
  useEffect(() => {
    if (product) {
      document.title = `${product.name} | ${product.brand} | Leisure Wellness Naples`;
    } else {
      document.title = 'Product Not Found | Leisure Wellness Naples';
    }
  }, [product]);
  
  useEffect(() => {
    if (product) {
      setSelectedShell(product.shellColors?.[0] || 'white');
      setSelectedCabinet(product.cabinetColors?.[0] || 'coastalGray');
      setSelectedCorner('match'); // Default to matching cabinet
      setCurrentView('color'); // Reset to color view
      setImageError(false);
      setSelectedCompareModel(null);
      setGalleryIndex(0); // Reset gallery index
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Reset imageError when color selections change
  useEffect(() => {
    setImageError(false);
  }, [selectedShell, selectedCabinet, selectedCorner]);
  
  const isGrandRiver = product?.brand === 'Grand River Spas';
  const isViking = product?.brand === 'Viking Spas';
  const isDynasty = product?.brand === 'Dynasty Spas';
  const isSaunaOrColdPlunge = product?.brand === 'SaunaLife' || product?.brand === 'Icebound' || 
    product?.brand === 'Finnmark Design' || product?.brand === 'Finsauna' || 
    product?.brand === 'Auroom' || product?.brand === 'Haljas' || product?.brand === 'Saunum' ||
    product?.brand === 'Cozy' || product?.brand === 'Huum' || product?.brand === 'Iki' || product?.brand === 'Narvi' ||
    product?.brand === 'Health Mate' || product?.brand === 'LeisureCraft' ||
    product?.category === 'outdoor' || product?.category === 'indoor' || product?.category === 'infrared' || 
    product?.category === 'plunge' || product?.category === 'heater' ||
    product?.category === 'sauna' || product?.category === 'redlight' || product?.category === 'furniture';
  const isSwimSpa = !!product?.length;
  const hasColorSelector = (isGrandRiver || (isViking && !!product?.colorVisualizerBase)) && !isSwimSpa;
  
  // Determine corner color options based on series
  const cornerOptions = useMemo(() => {
    if (!isViking || isSwimSpa) return [];
    const isElite = product?.series === 'Elite Series';
    return isElite 
      ? [{ key: 'match', name: 'Match Cabinet' }, { key: 'carbon', name: 'Carbon' }]
      : [{ key: 'match', name: 'Match Cabinet' }, { key: 'black', name: 'Black Slate' }];
  }, [isViking, isSwimSpa, product?.series]);
  
  // Determine available views based on brand - must be before early returns
  const views = useMemo(() => {
    if (isDynasty || isSaunaOrColdPlunge || isSwimSpa) return ['side'];
    if (hasColorSelector) return ['color', 'side', 'overhead'];
    return ['side', 'overhead'];
  }, [isDynasty, isSaunaOrColdPlunge, isSwimSpa, hasColorSelector]);
  
  const currentImage = useMemo(() => {
    if (!product) return ASSETS.logo;
    
    // For Dynasty Spas - only show primary image
    if (isDynasty) {
      return product.images.primary;
    }
    
    // For Saunas/Cold Plunge with gallery images
    if (isSaunaOrColdPlunge && galleryImages.length > 0) {
      return galleryImages[galleryIndex] || product.images.primary;
    }
    
    if (currentView === 'overhead') {
      return product.images.overhead;
    }
    if (currentView === 'side') {
      return product.images.primary;
    }
    
    // Color visualizer for Grand River
    if (isGrandRiver && !imageError) {
      const colorComboUrl = getGRColorComboImageUrl(product, selectedShell, selectedCabinet, selectedCorner);
      if (colorComboUrl) {
        return colorComboUrl;
      }
    }
    
    // Color visualizer for Viking Spas
    if (isViking && !imageError) {
      const colorComboUrl = getVSColorComboImageUrl(product, selectedShell, selectedCabinet, selectedCorner);
      if (colorComboUrl) {
        return colorComboUrl;
      }
    }
    
    return product.images.primary;
  }, [product, currentView, selectedShell, selectedCabinet, selectedCorner, isGrandRiver, isViking, isDynasty, imageError, isSaunaOrColdPlunge, galleryImages, galleryIndex]);
  
  if (!product) {
    return (
      <div className="pt-36 pb-20 text-center" style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)'
      }}>
        <h2 className="text-2xl font-bold text-[#0E3D95]">Product not found</h2>
        <Link to="/hot-tubs" className="text-[#F88200] hover:underline mt-4 inline-block">
          Return to Hot Tubs
        </Link>
      </div>
    );
  }

  const specs = isSaunaOrColdPlunge ? [
    ...(product.persons ? [{ icon: Users, label: 'Capacity', value: `${product.persons} ${product.persons === 1 ? 'Person' : 'Persons'}` }] : []),
    ...(product.dimensions ? [{ icon: Ruler, label: 'Dimensions', value: product.dimensions }] : []),
    ...(product.electrical ? [{ icon: Zap, label: 'Electrical', value: product.electrical }] : []),
    ...(product.heater ? [{ icon: Zap, label: 'Heater', value: product.heater }] : []),
    ...(product.heatingType ? [{ icon: Zap, label: 'Heating Type', value: product.heatingType }] : []),
    ...(product.wood ? [{ icon: Ruler, label: 'Wood', value: product.wood }] : []),
    ...(product.maxTemp ? [{ icon: Zap, label: 'Max Temp', value: product.maxTemp }] : []),
    ...(product.weight ? [{ icon: Ruler, label: 'Weight', value: product.weight }] : []),
    ...(product.waterCapacity ? [{ icon: Droplets, label: 'Water Capacity', value: product.waterCapacity }] : []),
  ] : [
    { icon: Users, label: 'Seats', value: `${product.persons} Adults` },
    { icon: Droplets, label: 'Jets', value: product.jets },
    { icon: Zap, label: 'Electrical', value: product.electrical },
    { icon: Ruler, label: 'Dimensions', value: product.dimensions },
    ...(product.waterCapacity ? [{ icon: Droplets, label: 'Water Capacity', value: product.waterCapacity }] : []),
    ...(product.dryWeight ? [{ icon: Ruler, label: 'Dry Weight', value: product.dryWeight }] : []),
    ...(product.filledWeight ? [{ icon: Ruler, label: 'Filled Weight', value: product.filledWeight }] : []),
    ...(product.pumps ? [{ icon: Zap, label: 'Pumps', value: product.pumps }] : []),
  ];
  
  const currentViewIndex = views.indexOf(currentView);
  
  const handlePrevView = () => {
    const newIndex = currentViewIndex > 0 ? currentViewIndex - 1 : views.length - 1;
    setCurrentView(views[newIndex]);
  };
  
  const handleNextView = () => {
    const newIndex = currentViewIndex < views.length - 1 ? currentViewIndex + 1 : 0;
    setCurrentView(views[newIndex]);
  };
  
  // Gallery navigation for sauna/cold plunge products
  const handlePrevGallery = () => {
    setGalleryIndex(prev => prev > 0 ? prev - 1 : galleryImages.length - 1);
  };
  
  const handleNextGallery = () => {
    setGalleryIndex(prev => prev < galleryImages.length - 1 ? prev + 1 : 0);
  };

  // SEO metadata
  const seoTitle = product ? `${product.name} by ${product.brand} | ${product.persons}-Person Hot Tub | Leisure Wellness Naples` : '';
  const seoDescription = product ? `${product.name} by ${product.brand}. ${product.description} Seats ${product.persons} adults with ${product.jets} jets. American Made & Proud of It.` : '';
  const seoKeywords = product ? `${product.name}, ${product.brand}, ${product.series}, hot tub, spa, ${product.persons} person hot tub, hydrotherapy` : '';
  const productId = product ? (product.id || id) : '';
  const canonicalUrl = product ? `https://www.leisurewellnessnaples.com/products/${productId}` : '';

  return (
    <>
      {product && (
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <meta name="keywords" content={seoKeywords} />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:type" content="product" />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
      )}
      <div className="pt-24 md:pt-36 pb-10 md:pb-20" data-testid="product-detail-page" style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)'
      }}>
        <div className="max-w-7xl mx-auto px-2 md:px-8">
          {/* Breadcrumb - Hidden on mobile */}
          <nav className="mb-2 md:mb-6 hidden md:block">
            <ol className="flex items-center gap-2 text-sm">
              <li><Link to="/" className="text-slate-500 hover:text-[#F88200]">Home</Link></li>
              <ChevronRight size={14} className="text-slate-400" />
              <li><Link to="/hot-tubs" className="text-slate-500 hover:text-[#F88200]">Hot Tubs</Link></li>
              <ChevronRight size={14} className="text-slate-400" />
              <li className="text-[#0E3D95] font-semibold">{product.name}</li>
            </ol>
          </nav>
          
          {/* Product Name Header - More compact on mobile */}
          <div className="mb-2 md:mb-4">
            <p className="text-xs md:text-sm text-slate-500 uppercase tracking-wider">{product.brand}</p>
            <p className="text-xs md:text-sm text-[#F88200] font-semibold uppercase tracking-wider">{product.series}</p>
            <h1 className="font-['Barlow_Condensed'] text-2xl md:text-5xl font-bold uppercase text-[#0E3D95]">
              {product.name}
            </h1>
            <p className="text-base md:text-lg font-bold text-[#F88200] mt-1">Call or email for pricing</p>
          </div>
          
          {/* Main Product Section - Image + Color Selector Side by Side */}
          <div className="grid lg:grid-cols-12 gap-2 md:gap-6 mb-4 md:mb-8">
            {/* Image Gallery - Smaller on mobile */}
            <div className={isSaunaOrColdPlunge ? 'lg:col-span-12' : 'lg:col-span-7'}>
              <div className="bg-slate-50 relative aspect-square md:aspect-[4/3] overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={`${currentImage}-${selectedShell}-${selectedCabinet}-${selectedCorner}`}
                    src={currentImage}
                    alt={`${product.name} - ${SHELL_COLORS[selectedShell]?.name} shell with ${CABINET_COLORS[selectedCabinet]?.name} cabinet`}
                    className="w-full h-full object-contain p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onError={(e) => {
                      if (currentView === 'color') {
                        setImageError(true);
                        e.target.src = product.images.primary;
                      }
                    }}
                  />
                </AnimatePresence>
                
                {/* Navigation Arrows - show for multiple views or gallery images */}
                {(views.length > 1 || (isSaunaOrColdPlunge && galleryImages.length > 1)) && (
                  <>
                    <button
                      onClick={isSaunaOrColdPlunge && galleryImages.length > 1 ? handlePrevGallery : handlePrevView}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 shadow-lg transition-all rounded-full"
                      data-testid="prev-view-btn"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={isSaunaOrColdPlunge && galleryImages.length > 1 ? handleNextGallery : handleNextView}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 shadow-lg transition-all rounded-full"
                      data-testid="next-view-btn"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}
                
                {/* Image Counter for Gallery */}
                {isSaunaOrColdPlunge && galleryImages.length > 1 && (
                  <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                    {galleryIndex + 1} / {galleryImages.length}
                  </div>
                )}
                
                {/* Color indicator badges */}
                {currentView === 'color' && hasColorSelector && (
                  <div className="absolute bottom-3 left-3 flex flex-wrap gap-2 z-10">
                    <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow-lg text-xs font-medium">
                      <div className="w-3 h-3 rounded-full border border-slate-200" style={{ backgroundColor: SHELL_COLORS[selectedShell]?.hex }} />
                      <span>Shell: {SHELL_COLORS[selectedShell]?.name}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow-lg text-xs font-medium">
                      <div className="w-3 h-3 rounded-full border border-slate-200" style={{ backgroundColor: CABINET_COLORS[selectedCabinet]?.hex }} />
                      <span>Cabinet: {CABINET_COLORS[selectedCabinet]?.name}</span>
                    </div>
                    {isViking && selectedCorner !== 'match' && (
                      <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full shadow-lg text-xs font-medium">
                        <div className="w-3 h-3 rounded-full border border-slate-200" style={{ backgroundColor: CORNER_COLORS[selectedCorner]?.hex || '#333' }} />
                        <span>Corner: {CORNER_COLORS[selectedCorner]?.name}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Gallery Thumbnails - For Saunas/Cold Plunge with multiple images */}
              {isSaunaOrColdPlunge && galleryImages.length > 1 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                  {galleryImages.map((img, idx) => (
                    <button
                      key={`gallery-thumb-${img}-${idx}`}
                      onClick={() => setGalleryIndex(idx)}
                      className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 border-2 rounded overflow-hidden transition-all ${
                        galleryIndex === idx 
                          ? 'border-[#F88200] ring-2 ring-[#F88200]/30' 
                          : 'border-slate-200 hover:border-[#0E3D95]'
                      }`}
                      data-testid={`gallery-thumb-${idx}`}
                    >
                      <img 
                        src={img} 
                        alt={`${product.name} view ${idx + 1}`} 
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
              
              {/* Product Video - YouTube Embed for Saunas with video */}
              {isSaunaOrColdPlunge && product?.images?.video && (
                <div className="mt-6">
                  <h3 className="text-lg font-bold text-[#0E3D95] mb-3 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                    Watch Product Video
                  </h3>
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-lg">
                    {/^https?:\/\//.test(product.images.video) && !/\.(mp4|webm|mov)(\?|$)/i.test(product.images.video) ? (
                      <iframe
                        src={product.images.video.replace('watch?v=', 'embed/')}
                        title={`${product.name} Video`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute top-0 left-0 w-full h-full"
                      ></iframe>
                    ) : (
                      <video
                        src={product.images.video}
                        controls
                        playsInline
                        preload="metadata"
                        className="absolute top-0 left-0 w-full h-full object-cover bg-black"
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                </div>
              )}
              
              {/* View Tabs & Thumbnails - Only show for hot tubs with multiple views */}
              {!isDynasty && !isSaunaOrColdPlunge && !isSwimSpa && (
                <div className="flex border border-slate-200 mt-2">
                  {hasColorSelector && (
                    <button
                      onClick={() => setCurrentView('color')}
                      className={`flex-1 py-2 font-semibold text-xs uppercase tracking-wider transition-colors ${
                        currentView === 'color' ? 'bg-[#F88200] text-white' : 'bg-white text-[#0E3D95] hover:bg-slate-50'
                      }`}
                      data-testid="view-color-btn"
                    >
                      Color Preview
                    </button>
                  )}
                  <button
                    onClick={() => setCurrentView('side')}
                    className={`flex-1 py-2 font-semibold text-xs uppercase tracking-wider transition-colors ${
                      currentView === 'side' ? 'bg-[#F88200] text-white' : 'bg-white text-[#0E3D95] hover:bg-slate-50'
                    }`}
                    data-testid="view-side-btn"
                  >
                    Side View
                  </button>
                  <button
                    onClick={() => setCurrentView('overhead')}
                    className={`flex-1 py-2 font-semibold text-xs uppercase tracking-wider transition-colors ${
                      currentView === 'overhead' ? 'bg-[#F88200] text-white' : 'bg-white text-[#0E3D95] hover:bg-slate-50'
                    }`}
                    data-testid="view-overhead-btn"
                  >
                    Overhead View
                  </button>
                </div>
              )}
            </div>
            
            {/* Color Selector Panel - 5 columns */}
            {!isSaunaOrColdPlunge && (
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              {hasColorSelector ? (
                <div className="bg-slate-50 p-3 md:p-5 h-full">
                  <h3 className="font-['Barlow_Condensed'] text-base md:text-xl font-bold uppercase text-[#0E3D95] mb-2 md:mb-4">
                    Customize Colors
                  </h3>
                  
                  {/* Shell Colors - Compact on mobile */}
                  <div className="mb-3 md:mb-5">
                    <p className="text-xs md:text-sm font-semibold text-slate-600 mb-1 md:mb-2">Shell Color</p>
                    <div className="flex gap-1.5 md:gap-2 flex-wrap">
                      {product.shellColors.map((colorKey) => {
                        const color = getShellColorData(colorKey);
                        if (!color) return null;
                        const isSelected = selectedShell === colorKey;
                        const swatchSize = isDynasty ? 'w-16 h-16 md:w-20 md:h-20' : 'w-10 h-10 md:w-14 md:h-14';
                        
                        return (
                          <motion.button
                            key={colorKey}
                            onClick={() => {
                              setSelectedShell(colorKey);
                              if (currentView !== 'color' && hasColorSelector) setCurrentView('color');
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="relative group"
                            data-testid={`shell-color-${colorKey}`}
                          >
                            <div className={`${swatchSize} rounded-lg overflow-hidden border-2 transition-all ${
                              isSelected ? 'border-[#F88200] ring-2 ring-[#F88200] ring-offset-1' : 'border-slate-200 hover:border-slate-400'
                            }`}>
                              {color.image ? (
                                <img src={color.image} alt={color.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = color.hex; }} />
                              ) : (
                                <div className="w-full h-full" style={{ backgroundColor: color.hex }} />
                              )}
                              {isSelected && (
                                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute inset-0 bg-[#F88200]/20 flex items-center justify-center rounded-lg">
                                  <Check className="text-[#F88200]" size={14} strokeWidth={3} />
                                </motion.div>
                              )}
                            </div>
                            <p className={`text-[9px] md:text-[10px] mt-0.5 md:mt-1 text-center font-medium ${isDynasty ? 'max-w-16 md:max-w-20' : 'max-w-10 md:max-w-14'} ${isSelected ? 'text-[#F88200]' : 'text-slate-600'}`}>
                              {isDynasty ? color.name : color.name.split(' ')[0]}
                            </p>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Cabinet Colors - Compact on mobile */}
                  <div className="mb-3 md:mb-5">
                    <p className="text-xs md:text-sm font-semibold text-slate-600 mb-1 md:mb-2">{isDynasty ? 'Skirt Color' : 'Cabinet Color'}</p>
                    <div className="flex gap-1.5 md:gap-2 flex-wrap">
                      {product.cabinetColors.map((colorKey) => {
                        const color = getCabinetColorData(colorKey);
                        if (!color) return null;
                        const isSelected = selectedCabinet === colorKey;
                        const swatchSize = isDynasty ? 'w-16 h-16 md:w-20 md:h-20' : 'w-10 h-10 md:w-14 md:h-14';
                        
                        return (
                          <motion.button
                            key={colorKey}
                            onClick={() => {
                              setSelectedCabinet(colorKey);
                              if (currentView !== 'color' && hasColorSelector) setCurrentView('color');
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="relative group"
                            data-testid={`cabinet-color-${colorKey}`}
                          >
                            <div className={`${swatchSize} rounded-lg overflow-hidden border-2 transition-all ${
                              isSelected ? 'border-[#F88200] ring-2 ring-[#F88200] ring-offset-1' : 'border-slate-200 hover:border-slate-400'
                            }`}>
                              {color.image ? (
                                <img src={color.image} alt={color.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = color.hex; }} />
                              ) : (
                                <div className="w-full h-full" style={{ backgroundColor: color.hex }} />
                              )}
                              {isSelected && (
                                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute inset-0 bg-[#F88200]/20 flex items-center justify-center rounded-lg">
                                  <Check className="text-[#F88200]" size={14} strokeWidth={3} />
                                </motion.div>
                              )}
                            </div>
                            <p className={`text-[9px] md:text-[10px] mt-0.5 md:mt-1 text-center font-medium ${isDynasty ? 'max-w-16 md:max-w-20' : 'max-w-10 md:max-w-14'} ${isSelected ? 'text-[#F88200]' : 'text-slate-600'}`}>
                              {isDynasty ? color.name : color.name.split(' ')[0]}
                            </p>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Corner Colors - Viking Spas specific */}
                  {isViking && (
                    <div className="mb-3 md:mb-5">
                      <p className="text-xs md:text-sm font-semibold text-slate-600 mb-1 md:mb-2">Corner Color</p>
                      <div className="flex gap-2 md:gap-3 flex-wrap">
                        {cornerOptions.map((option) => {
                          const isSelected = selectedCorner === option.key;
                          const cornerColor = option.key === 'match' 
                            ? CABINET_COLORS[selectedCabinet] 
                            : CORNER_COLORS[option.key];
                          
                          return (
                            <motion.button
                              key={option.key}
                              onClick={() => {
                                setSelectedCorner(option.key);
                                if (currentView !== 'color') setCurrentView('color');
                              }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="relative group"
                              data-testid={`corner-color-${option.key}`}
                            >
                              <div className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                                isSelected ? 'border-[#F88200] ring-2 ring-[#F88200] ring-offset-1' : 'border-slate-200 hover:border-slate-400'
                              }`}>
                                {cornerColor?.image ? (
                                  <img src={cornerColor.image} alt={option.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.backgroundColor = cornerColor?.hex; }} />
                                ) : (
                                  <div className="w-full h-full" style={{ backgroundColor: cornerColor?.hex }} />
                                )}
                                {isSelected && (
                                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute inset-0 bg-[#F88200]/20 flex items-center justify-center rounded-lg">
                                    <Check className="text-[#F88200]" size={16} strokeWidth={3} />
                                  </motion.div>
                                )}
                              </div>
                              <p className={`text-[10px] mt-1 text-center font-medium max-w-14 ${isSelected ? 'text-[#F88200]' : 'text-slate-600'}`}>
                                {option.name}
                              </p>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Corner Colors - Grand River (Match Cabinet or Black) */}
                  {isGrandRiver && (
                    <div className="mb-5">
                      <p className="text-sm font-semibold text-slate-600 mb-2">Corner Color</p>
                      <div className="flex gap-3 flex-wrap">
                        {/* Match Cabinet Option */}
                        <motion.button
                          onClick={() => {
                            setSelectedCorner('match');
                            if (currentView !== 'color') setCurrentView('color');
                          }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="relative group"
                          data-testid="corner-color-match"
                        >
                          <div className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                            selectedCorner === 'match' ? 'border-[#F88200] ring-2 ring-[#F88200] ring-offset-1' : 'border-slate-200 hover:border-slate-400'
                          }`}>
                            <div className="w-full h-full" style={{ backgroundColor: CABINET_COLORS[selectedCabinet]?.hex }} />
                            {selectedCorner === 'match' && (
                              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute inset-0 bg-[#F88200]/20 flex items-center justify-center rounded-lg">
                                <Check className="text-[#F88200]" size={16} strokeWidth={3} />
                              </motion.div>
                            )}
                          </div>
                          <p className={`text-[10px] mt-1 text-center font-medium max-w-14 ${selectedCorner === 'match' ? 'text-[#F88200]' : 'text-slate-600'}`}>
                            Match Cabinet
                          </p>
                        </motion.button>
                        
                        {/* Black Slate Option */}
                        <motion.button
                          onClick={() => {
                            setSelectedCorner('black');
                            if (currentView !== 'color') setCurrentView('color');
                          }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="relative group"
                          data-testid="corner-color-black"
                        >
                          <div className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                            selectedCorner === 'black' ? 'border-[#F88200] ring-2 ring-[#F88200] ring-offset-1' : 'border-slate-200 hover:border-slate-400'
                          }`}>
                            <div className="w-full h-full" style={{ backgroundColor: '#1a1a1a' }} />
                            {selectedCorner === 'black' && (
                              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute inset-0 bg-[#F88200]/20 flex items-center justify-center rounded-lg">
                                <Check className="text-[#F88200]" size={16} strokeWidth={3} />
                              </motion.div>
                            )}
                          </div>
                          <p className={`text-[10px] mt-1 text-center font-medium max-w-14 ${selectedCorner === 'black' ? 'text-[#F88200]' : 'text-slate-600'}`}>
                            Black Slate
                          </p>
                        </motion.button>
                      </div>
                    </div>
                  )}
                  
                  {/* Dynasty Selected Colors Summary */}
                  {isDynasty && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-sm font-semibold text-[#0E3D95] mb-2">Your Selection</p>
                      <div className="space-y-1">
                        <p className="text-sm text-slate-600">
                          Shell: <span className="font-semibold text-[#0E3D95]">{getShellColorData(selectedShell)?.name || selectedShell}</span>
                        </p>
                        <p className="text-sm text-slate-600">
                          Skirt: <span className="font-semibold text-[#0E3D95]">{getCabinetColorData(selectedCabinet)?.name || selectedCabinet}</span>
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-500 mt-4">
                    Actual colors may vary. Contact us for physical color samples.
                  </p>
                  
                  {/* Quick Specs in Color Panel */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="grid grid-cols-2 gap-2">
                      {specs.map((spec) => (
                        <div key={spec.label} className="flex items-center gap-2 text-sm">
                          <spec.icon size={14} className="text-[#F88200]" />
                          <span className="text-slate-600">{spec.label}:</span>
                          <span className="font-semibold text-[#0E3D95]">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 p-5 h-full">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0E3D95] mb-4">
                    Quick Specs
                  </h3>
                  <div className="space-y-3">
                    {specs.map((spec) => (
                      <div key={spec.label} className="flex items-center gap-3">
                        <spec.icon size={18} className="text-[#F88200]" />
                        <span className="text-slate-600">{spec.label}:</span>
                        <span className="font-semibold text-[#0E3D95]">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Sauna / Cold Plunge - Quick Specs Section (since color panel is hidden) */}
          {isSaunaOrColdPlunge && specs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-slate-200 rounded-lg shadow-md mb-8 overflow-hidden"
              data-testid="sauna-quick-specs"
            >
              <div className="bg-gradient-to-r from-[#0E3D95] to-[#1a4d8a] p-4">
                <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase text-white">
                  Quick Specs
                </h3>
              </div>
              <div className="p-5 md:p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {specs.map((spec) => (
                    <div key={spec.label} className="flex items-start gap-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
                      <spec.icon size={18} className="text-[#F88200] flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{spec.label}</p>
                        <p className="font-bold text-[#0E3D95] text-sm md:text-base break-words">{spec.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {product.brand && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-3 text-sm">
                    <span className="text-slate-500">Brand: <span className="font-semibold text-[#0E3D95]">{product.brand}</span></span>
                    {product.series && (
                      <span className="text-slate-500">Series: <span className="font-semibold text-[#0E3D95]">{product.series}</span></span>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
          
          {/* Product Description Section */}
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Left - Description */}
            <div>
              <p className="text-slate-600 leading-relaxed mb-6">
                {product.longDescription || product.description}
              </p>
              
              {/* CTA */}
              <div className="flex flex-wrap gap-3 mb-6">
                <a 
                  href={`tel:${CONTACT.phone}`} 
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="call-btn"
                >
                  <Phone size={18} />
                  Call for Best Price
                </a>
                <Link to="/contact" className="btn-secondary" data-testid="quote-btn">
                  Get a Quote
                </Link>
                {/* AR Visualizer - See spa in your space */}
                <Link
                  to={`/ar-visualizer?product=${encodeURIComponent(product.id)}`}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F88200] to-[#ff9d3c] hover:from-[#e07600] hover:to-[#F88200] text-white font-bold py-3 px-5 transition-colors border border-[#F88200] rounded-lg shadow-md hover:shadow-lg"
                  data-testid="ar-visualize-btn"
                  aria-label={`See ${product.name} in your space with AR`}
                >
                  <Smartphone size={18} />
                  See it in Your Space (AR)
                </Link>
                {/* Brochure/Spec Sheet Download - for all brands */}
                {productBrochure && (
                  <a 
                    href={productBrochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#98FB98] via-[#FFFACD] to-white hover:from-[#7CFC00] hover:via-[#FFD700] text-[#0E3D95] font-bold py-3 px-5 transition-colors border border-[#98FB98] rounded-lg"
                    data-testid="brochure-btn"
                  >
                    <Download size={18} />
                    {product?.specSheet ? 'Download Spec Sheet' : 'Download Brochure'}
                  </a>
                )}
                {/* Additional downloads (manuals, guides) */}
                {Array.isArray(product?.downloads) && product.downloads.filter(d => d.file !== product?.specSheet).map((dl) => (
                  <a
                    key={dl.file}
                    href={dl.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-[#0E3D95] font-bold py-3 px-5 transition-colors border border-slate-200 rounded-lg"
                    data-testid={`download-${dl.file}`}
                  >
                    <Download size={18} />
                    {dl.label}
                  </a>
                ))}
              </div>

              {/* Shipping & Installation note */}
              {product?.shippingNote && (
                <div className="flex items-start gap-2 text-sm text-slate-700 bg-[#FFF7ED] border border-[#F88200]/30 rounded-lg p-3" data-testid="shipping-note">
                  <Info size={18} className="text-[#F88200] mt-0.5 shrink-0" />
                  <span>{product.shippingNote}</span>
                </div>
              )}
              
              {/* American Made Badge */}
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span className="text-lg">🇺🇸</span>
                <span className="font-semibold">American Made & Proud of It</span>
              </div>
            </div>
            
            {/* Right - Features */}
            <div>
              {product.features && (
                <div className="bg-slate-50 p-6">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-[#0E3D95] mb-4">
                    Key Features
                  </h3>
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={`feature-${feature.slice(0, 20).replace(/\s/g, '-')}-${index}`} className="flex items-start gap-2">
                        <Check size={16} className="text-[#F88200] mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          
          {/* Grand River Spas - Full Specifications Section */}
          {isGrandRiver && product.fullSpecs && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
              data-testid="grand-river-full-specs"
            >
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-[#0E3D95] to-[#1a4d8a] p-4">
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white text-center">
                    Full Specifications
                  </h3>
                </div>
                <div className="p-6">
                  {/* Primary Specs Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#F88200]">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Seats</p>
                      <p className="text-lg font-bold text-[#0E3D95]">{product.fullSpecs.seats}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#F88200]">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Seating Type</p>
                      <p className="text-lg font-bold text-[#0E3D95]">{product.fullSpecs.seatingType}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#F88200]">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Total Jets</p>
                      <p className="text-lg font-bold text-[#0E3D95]">{product.fullSpecs.totalJets}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#F88200]">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Jet Pumps</p>
                      <p className="text-lg font-bold text-[#0E3D95]">{product.fullSpecs.jetPumps}</p>
                    </div>
                  </div>

                  {/* Dimensions & Capacity */}
                  <div className="mb-6">
                    <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                      Dimensions & Capacity
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Dimensions</span>
                        <span className="font-semibold text-slate-800">{product.fullSpecs.dimensions}</span>
                      </div>
                      {product.fullSpecs.dimensionsMetric && (
                        <div className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-600">Dimensions (Metric)</span>
                          <span className="font-semibold text-slate-800">{product.fullSpecs.dimensionsMetric}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Water Capacity</span>
                        <span className="font-semibold text-slate-800">{product.fullSpecs.waterCapacity}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Dry Weight</span>
                        <span className="font-semibold text-slate-800">{product.fullSpecs.dryWeight}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Filled Weight</span>
                        <span className="font-semibold text-slate-800">{product.fullSpecs.filledWeight}</span>
                      </div>
                    </div>
                  </div>

                  {/* Jets Breakdown */}
                  {product.fullSpecs.jets && product.fullSpecs.jets.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                        Jets Breakdown
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {product.fullSpecs.jets.map((jet, index) => (
                          <div key={`jet-${jet.slice(0, 20).replace(/\s/g, '-')}-${index}`} className="flex items-center gap-2 py-1">
                            <span className="w-2 h-2 rounded-full bg-[#F88200]"></span>
                            <span className="text-slate-700 text-sm">{jet}</span>
                          </div>
                        ))}
                      </div>
                      {product.fullSpecs.auxiliaryJets && (
                        <div className="mt-3 pt-2 border-t border-slate-100">
                          <span className="text-slate-600 text-sm">Auxiliary Jets: </span>
                          <span className="font-semibold text-slate-800 text-sm">{product.fullSpecs.auxiliaryJets}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Systems & Features */}
                  <div className="mb-6">
                    <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                      Systems & Features
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Control System</span>
                        <span className="font-semibold text-slate-800 text-right text-sm">{product.fullSpecs.controlSystem}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Heater</span>
                        <span className="font-semibold text-slate-800">{product.fullSpecs.heater}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Lighting System</span>
                        <span className="font-semibold text-slate-800 text-right text-sm">{product.fullSpecs.lightingSystem}</span>
                      </div>
                      {product.fullSpecs.waterFeature && product.fullSpecs.waterFeature !== 'N/A' && (
                        <div className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-600">Water Feature</span>
                          <span className="font-semibold text-slate-800">{product.fullSpecs.waterFeature}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Filtration</span>
                        <span className="font-semibold text-slate-800">{product.fullSpecs.filtration}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Water Care</span>
                        <span className="font-semibold text-slate-800 text-right text-sm">{product.fullSpecs.waterCare}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Performance Rating</span>
                        <span className="font-semibold text-slate-800">{product.fullSpecs.performanceRating}</span>
                      </div>
                    </div>
                  </div>

                  {/* Energy Efficiency */}
                  {product.fullSpecs.energyEfficiency && (
                    <div className="mb-6">
                      <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                        Energy Efficiency
                      </h4>
                      <p className="text-slate-700 text-sm bg-green-50 p-3 rounded-lg border border-green-200">
                        {product.fullSpecs.energyEfficiency}
                      </p>
                    </div>
                  )}

                  {/* Color Options */}
                  <div className="mb-6">
                    <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                      Available Colors
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Shell Colors</p>
                        <p className="text-sm font-semibold text-slate-800">{product.fullSpecs.shellColorOptions}</p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Cabinet Colors</p>
                        <p className="text-sm font-semibold text-slate-800">{product.fullSpecs.cabinetColorOptions}</p>
                      </div>
                      {product.fullSpecs.cornerColorOptions && product.fullSpecs.cornerColorOptions !== 'N/A' && (
                        <div className="bg-slate-50 p-3 rounded-lg">
                          <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Corner Colors</p>
                          <p className="text-sm font-semibold text-slate-800">{product.fullSpecs.cornerColorOptions}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Spa Cover */}
                  {product.fullSpecs.spaCover && (
                    <div>
                      <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                        Included Spa Cover
                      </h4>
                      <p className="text-slate-700 text-sm">{product.fullSpecs.spaCover}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Dynasty Spas - Full Specifications Section */}
          {isDynasty && !isSwimSpa && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
              data-testid="dynasty-full-specs"
            >
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-[#0E3D95] to-[#1a4d8a] p-4">
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white text-center">
                    Full Specifications
                  </h3>
                </div>
                <div className="p-6">
                  {/* Primary Specs Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#F88200]">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Seats</p>
                      <p className="text-lg font-bold text-[#0E3D95]">{product.persons} Adults</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#F88200]">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Seating Type</p>
                      <p className="text-lg font-bold text-[#0E3D95]">{product.seatingLayout}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#F88200]">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Total Jets</p>
                      <p className="text-lg font-bold text-[#0E3D95]">{product.jets}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-[#F88200]">
                      <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Pumps</p>
                      <p className="text-lg font-bold text-[#0E3D95]">{product.pumps}</p>
                    </div>
                  </div>

                  {/* Dimensions & Capacity */}
                  <div className="mb-6">
                    <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                      Dimensions & Capacity
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Dimensions</span>
                        <span className="font-semibold text-slate-800">{product.dimensions}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Water Capacity</span>
                        <span className="font-semibold text-slate-800">{product.waterCapacity}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Dry Weight</span>
                        <span className="font-semibold text-slate-800">{product.dryWeight}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Filled Weight</span>
                        <span className="font-semibold text-slate-800">{product.filledWeight}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Electrical</span>
                        <span className="font-semibold text-slate-800">{product.electrical}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Filtration</span>
                        <span className="font-semibold text-slate-800">{product.filtration}</span>
                      </div>
                    </div>
                  </div>

                  {/* Systems & Features */}
                  <div className="mb-6">
                    <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                      Standard Features
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {product.features && product.features.map((feature, index) => (
                        <div key={`ds-feature-${feature.slice(0, 20).replace(/\s/g, '-')}-${index}`} className="flex items-center gap-2 py-1">
                          <span className="w-2 h-2 rounded-full bg-[#F88200]"></span>
                          <span className="text-slate-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Collection Info */}
                  <div className="mb-6">
                    <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                      Collection & Build
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Collection</span>
                        <span className="font-semibold text-slate-800">{product.series}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Brand</span>
                        <span className="font-semibold text-slate-800">Dynasty Spas</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Made In</span>
                        <span className="font-semibold text-slate-800">USA - Athens, TN</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-100">
                        <span className="text-slate-600">Insulation</span>
                        <span className="font-semibold text-slate-800">RMAX® Insulation</span>
                      </div>
                    </div>
                  </div>

                  {/* Available Colors */}
                  <div className="mb-6">
                    <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                      Available Colors
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Shell Colors</p>
                        <p className="text-sm font-semibold text-slate-800">
                          {product.shellColors && product.shellColors.map(c => {
                            const colorNames = { sterlingMarble: 'Sterling Marble', stormClouds: 'Storm Clouds', tuscanSun: 'Tuscan Sun', smokyMountain: 'Smoky Mountain' };
                            return colorNames[c] || c;
                          }).join(', ')}
                        </p>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Cabinet Colors</p>
                        <p className="text-sm font-semibold text-slate-800">
                          {product.cabinetColors && product.cabinetColors.map(c => {
                            const colorNames = { blackConfer: 'Black Confer', grayConfer: 'Gray Confer', ashElite: 'Ash Elite', coastalGrayElite: 'Coastal Gray Elite' };
                            return colorNames[c] || c;
                          }).join(', ')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Warranty */}
                  <div>
                    <h4 className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-[#0E3D95] mb-3 border-b border-slate-200 pb-2">
                      Dynasty Spas Warranty
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-center">
                        <p className="text-green-800 font-bold">20-Year Shell</p>
                        <p className="text-green-600 text-sm">Non-Prorated</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-center">
                        <p className="text-green-800 font-bold">5-Year Structure</p>
                        <p className="text-green-600 text-sm">Framework & Support</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-center">
                        <p className="text-green-800 font-bold">3-Year Parts</p>
                        <p className="text-green-600 text-sm">Plumbing & Equipment</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          
          {/* Grand River Spas - Available Extras/Upgrades Section */}
          {isGrandRiver && !isSwimSpa && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
              data-testid="grand-river-extras"
            >
              <div className="bg-gradient-to-r from-[#0E3D95] to-[#1a2d4a] p-6 lg:p-8">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-6 text-center">
                  Available Upgrades & Extras
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {GRAND_RIVER_EXTRAS.map((extra) => (
                    <div key={extra.id} className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                      {extra.image && (
                        <div className="w-full h-24 mb-3 flex items-center justify-center bg-white/5 rounded">
                          <img src={extra.image} alt={extra.name} className="max-h-full max-w-full object-contain rounded" />
                        </div>
                      )}
                      <h4 className="text-white font-bold text-center mb-2">{extra.name}</h4>
                      <p className="text-[#F88200] font-bold text-xl text-center">Call or email for pricing</p>
                      <p className="text-slate-300 text-xs text-center mt-2">{extra.description}</p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 text-sm text-center mt-4">
                  Ask your sales representative about adding these upgrades to your order.
                </p>
              </div>
            </motion.div>
          )}

          {/* Viking Spas - Free Items Banner */}
          {isViking && !isSwimSpa && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
              data-testid="viking-free-items"
            >
              <div className="bg-gradient-to-r from-green-700 to-green-900 p-6 lg:p-8 rounded-lg">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-4 text-center">
                  Included With Your Spa
                </h3>
                <div className="flex flex-wrap justify-center gap-4">
                  {VIKING_SPAS_FREE_ITEMS.items.map((item, idx) => (
                    <span key={`viking-free-${item.slice(0, 15).replace(/\s/g, '-')}-${idx}`} className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white font-medium text-sm">
                      ✓ {item}
                    </span>
                  ))}
                </div>
                <p className="text-green-100 text-center mt-4 text-sm">
                  {VIKING_SPAS_FREE_ITEMS.note}
                </p>
              </div>
            </motion.div>
          )}

          {/* Viking Spas - Available Extras/Upgrades Section */}
          {isViking && !isSwimSpa && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
              data-testid="viking-extras"
            >
              <div className="bg-gradient-to-r from-[#0E3D95] to-[#1a2d4a] p-6 lg:p-8">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-6 text-center">
                  Available Upgrades & Add-Ons
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {VIKING_SPAS_EXTRAS.map((extra) => (
                    <div key={extra.id} className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                      {extra.image && (
                        <div className="w-full h-24 mb-3 flex items-center justify-center bg-white/5 rounded">
                          <img src={extra.image} alt={extra.name} className="max-h-full max-w-full object-contain rounded" />
                        </div>
                      )}
                      <h4 className="text-white font-bold text-center mb-2">{extra.name}</h4>
                      <p className="text-[#F88200] font-bold text-xl text-center">Call or email for pricing</p>
                      <p className="text-slate-300 text-xs text-center mt-2">{extra.description}</p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 text-sm text-center mt-4">
                  Ask your sales representative about adding these upgrades to your order.
                </p>
              </div>
            </motion.div>
          )}

          {/* Sauna - White Glove Installation Option */}
          {product?.brand === 'SaunaLife' && product?.whiteGloveInstallation && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
              data-testid="sauna-installation"
            >
              <div className="bg-gradient-to-r from-amber-700 to-amber-900 p-6 lg:p-8 rounded-lg">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="text-center md:text-left">
                    <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-2">
                      White Glove Installation Available
                    </h3>
                    <p className="text-amber-100">
                      {SAUNA_INSTALLATION_OPTION.description}
                    </p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm px-8 py-4 rounded-lg border border-white/30 text-center">
                    <span className="text-white text-sm block mb-1">Add Professional Installation</span>
                    <span className="text-white font-bold text-lg">Call or email for pricing</span>
                  </div>
                </div>
                <p className="text-amber-200 text-sm text-center md:text-left mt-4">
                  Contact us to add white glove installation to your sauna order.
                </p>
              </div>
            </motion.div>
          )}

          {/* Dynasty Spas - Available Extras/Upgrades Section */}
          {isDynasty && !isSwimSpa && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
              data-testid="dynasty-extras"
            >
              <div className="bg-gradient-to-r from-[#0E3D95] to-[#1a2d4a] p-6 lg:p-8">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-6 text-center">
                  Available Upgrades & Extras for {product?.series}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {(product?.series === 'Luxury Collection' ? DYNASTY_LUXURY_EXTRAS :
                    product?.series === 'Oasis Collection' ? DYNASTY_OASIS_EXTRAS :
                    product?.series === 'Vacation Collection' ? DYNASTY_VACATION_EXTRAS :
                    product?.series === 'Hideaway Collection' ? DYNASTY_HIDEAWAY_EXTRAS :
                    product?.series === 'Family Collection' ? DYNASTY_FAMILY_EXTRAS :
                    product?.series === 'Aquex Collection' ? DYNASTY_AQUEX_EXTRAS : DYNASTY_LUXURY_EXTRAS).map((extra) => (
                    <div key={extra.id} className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                      {extra.image && (
                        <div className="w-full h-32 mb-3 flex items-center justify-center">
                          <img src={extra.image} alt={extra.name} className="max-h-full max-w-full object-contain" />
                        </div>
                      )}
                      <h4 className="text-white font-bold text-center mb-2">{extra.name}</h4>
                      <p className="text-[#F88200] font-bold text-xl text-center">Call or email for pricing</p>
                      <p className="text-slate-300 text-xs text-center mt-2">{extra.description}</p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-400 text-sm text-center mt-4">
                  Ask your sales representative about adding these upgrades to your order.
                </p>
              </div>
            </motion.div>
          )}

          {/* Aquora Plunge - Included Features Section */}
          {product?.series === 'Aquora Collection' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
              data-testid="aquora-features"
            >
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 lg:p-8 rounded-lg">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-6 text-center">
                  Included Features - {product?.name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {(product?.id === 'aquora-all-season' ? AQUORA_ALL_SEASON_FEATURES :
                    product?.id === 'aquora-heat' ? AQUORA_HEAT_FEATURES :
                    product?.id === 'aquora-complete' ? AQUORA_COMPLETE_FEATURES :
                    product?.id === 'aquora-core' ? AQUORA_CORE_FEATURES : []).map((feature) => (
                    <div key={feature.id} className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
                      {feature.image && (
                        <div className="w-full h-24 mb-3 flex items-center justify-center">
                          <img 
                            src={feature.image} 
                            alt={feature.name} 
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <h4 className="text-white font-bold text-center mb-1">{feature.name}</h4>
                      <p className="text-cyan-400 font-semibold text-sm text-center">Included</p>
                      <p className="text-slate-300 text-xs text-center mt-2">{feature.description}</p>
                    </div>
                  ))}
                </div>
                
                {/* Available Upgrades */}
                <div className="border-t border-white/20 pt-6 mt-6">
                  <h4 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 text-center">
                    Available Upgrades
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {AQUORA_EXTRAS.map((extra) => (
                      <div key={extra.id} className="bg-white/5 backdrop-blur-sm p-4 rounded-lg border border-white/10">
                        {extra.image && (
                          <div className="w-full h-20 mb-3 flex items-center justify-center">
                            <img 
                              src={extra.image} 
                              alt={extra.name} 
                              className="max-h-full max-w-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <h4 className="text-white font-bold text-center mb-1 text-sm">{extra.name}</h4>
                        <p className="text-[#F88200] font-bold text-center">Call or email for pricing</p>
                        <p className="text-slate-400 text-xs text-center mt-1">{extra.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                <p className="text-slate-400 text-sm text-center mt-6">
                  Contact us for current pricing and availability.
                </p>
              </div>
            </motion.div>
          )}

          {/* Dynasty Spas - Color Reference Section */}
          {isDynasty && product.shellColors && (() => {
            const hideawayModels = ['ds-bay-bliss', 'ds-high-tide', 'ds-seaside'];
            const isHideaway = hideawayModels.includes(product.id);
            return (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-8"
                data-testid="dynasty-color-reference"
              >
                <div className="bg-slate-50 p-6 lg:p-8">
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0E3D95] mb-6 text-center">
                    Available Color Options
                  </h3>
                  <img 
                    src={isHideaway ? '/assets/dynasty_colors_hideaway.png' : '/assets/dynasty_colors_luxury.png'}
                    alt={isHideaway ? "Acrylic Color: Sterling Marble, Skirt Color: Black Confer" : "Acrylic & Skirt Color Options"}
                    className="w-full max-w-3xl mx-auto rounded-lg shadow-md"
                    data-testid="dynasty-color-ref-img"
                  />
                  <p className="text-xs text-slate-500 mt-4 text-center">
                    Actual colors may vary. Contact us for physical color samples.
                  </p>
                </div>
              </motion.div>
            );
          })()}

          {/* Model Comparison for Grand River (existing related model) */}
          {relatedModel && isGrandRiver && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-6 mb-8"
            >
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0E3D95] mb-6 text-center">
                Compare Models
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Current Model */}
                <div className="bg-white p-4 border-2 border-[#F88200]">
                  <div className="text-center mb-4">
                    <span className="text-xs bg-[#F88200] text-white px-2 py-1 uppercase font-bold">Currently Viewing</span>
                  </div>
                  <img src={product.images.overhead || product.images.primary} alt={product.name} className="w-full h-40 object-contain mb-4" />
                  <h4 className="font-['Barlow_Condensed'] text-xl font-bold text-center text-[#0E3D95]">{product.name}</h4>
                  <p className="text-center text-[#F88200] font-bold text-lg">Call or email for pricing</p>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Dimensions:</span><span className="font-semibold">{product.dimensions}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Seats:</span><span className="font-semibold">{product.persons} Adults</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Jets:</span><span className="font-semibold text-[#F88200]">{product.jets}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Seating:</span><span className="font-semibold">{product.seatingLayout}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Electrical:</span><span className="font-semibold">{product.electrical}</span></div>
                  </div>
                </div>
                
                {/* Related Model */}
                <div className="bg-white p-4 border border-slate-200">
                  <img src={relatedModel.images.overhead || relatedModel.images.primary} alt={relatedModel.name} className="w-full h-40 object-contain mb-4 mt-6" />
                  <h4 className="font-['Barlow_Condensed'] text-xl font-bold text-center text-[#0E3D95]">{relatedModel.name}</h4>
                  <p className="text-center text-[#F88200] font-bold text-lg">Call or email for pricing</p>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Dimensions:</span><span className="font-semibold">{relatedModel.dimensions}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Seats:</span><span className="font-semibold">{relatedModel.persons} Adults</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Jets:</span><span className="font-semibold text-[#F88200]">{relatedModel.jets}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Seating:</span><span className="font-semibold">{relatedModel.seatingLayout}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Electrical:</span><span className="font-semibold">{relatedModel.electrical}</span></div>
                  </div>
                  <Link to={`/products/${relatedModel.id}`} className="block mt-4 text-center bg-slate-100 hover:bg-slate-200 py-2 text-[#0E3D95] font-semibold transition-colors">
                    View {relatedModel.name} <ArrowRight size={14} className="inline ml-1" />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Model Comparison for Viking Spas and Dynasty Spas */}
          {(isViking || isDynasty) && comparableModels.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-6 mb-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0E3D95] flex items-center gap-2">
                  <GitCompare size={24} className="text-[#F88200]" />
                  Compare Models
                </h3>
                <p className="text-sm text-slate-500">{comparableModels.length} other {product.brand} models available</p>
              </div>
              
              {/* Model Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Select a model to compare:</label>
                <select
                  value={selectedCompareModel?.id || ''}
                  onChange={(e) => {
                    const model = comparableModels.find(m => m.id === e.target.value);
                    setSelectedCompareModel(model || null);
                  }}
                  className="w-full md:w-1/2 border border-slate-300 px-4 py-3 bg-white text-sm focus:border-[#F88200] focus:outline-none"
                >
                  <option value="">-- Choose a model --</option>
                  {comparableModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name} - {model.persons} Person - {model.jets} Jets - Call or email for pricing
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Comparison Table */}
              {selectedCompareModel ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Current Model */}
                  <div className="bg-white p-4 border-2 border-[#F88200]">
                    <div className="text-center mb-4">
                      <span className="text-xs bg-[#F88200] text-white px-2 py-1 uppercase font-bold">Currently Viewing</span>
                    </div>
                    <img src={product.images.primary} alt={product.name} className="w-full h-48 object-contain mb-4" />
                    <h4 className="font-['Barlow_Condensed'] text-xl font-bold text-center text-[#0E3D95]">{product.name}</h4>
                    <p className="text-center text-slate-500 text-sm mb-2">{product.series}</p>
                    <p className="text-center text-[#F88200] font-bold text-xl">Call or email for pricing</p>
                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Dimensions:</span>
                        <span className="font-semibold">{product.dimensions}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Seats:</span>
                        <span className="font-semibold">{product.persons} Adults</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Jets:</span>
                        <span className="font-bold text-[#F88200]">{product.jets}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Seating:</span>
                        <span className="font-semibold">{product.seatingLayout}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Electrical:</span>
                        <span className="font-semibold">{product.electrical}</span>
                      </div>
                      {product.waterCapacity && (
                        <div className="flex justify-between border-b border-slate-100 pb-2">
                          <span className="text-slate-500">Water Capacity:</span>
                          <span className="font-semibold">{product.waterCapacity}</span>
                        </div>
                      )}
                      {product.dryWeight && (
                        <div className="flex justify-between border-b border-slate-100 pb-2">
                          <span className="text-slate-500">Dry Weight:</span>
                          <span className="font-semibold">{product.dryWeight}</span>
                        </div>
                      )}
                      {product.filledWeight && (
                        <div className="flex justify-between border-b border-slate-100 pb-2">
                          <span className="text-slate-500">Filled Weight:</span>
                          <span className="font-semibold">{product.filledWeight}</span>
                        </div>
                      )}
                      {product.pumps && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Pumps:</span>
                          <span className="font-semibold">{product.pumps}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Selected Compare Model */}
                  <div className="bg-white p-4 border border-slate-200 hover:border-slate-400 transition-colors">
                    <div className="text-center mb-4">
                      <span className="text-xs bg-slate-600 text-white px-2 py-1 uppercase font-bold">Comparing To</span>
                    </div>
                    <img src={selectedCompareModel.images.primary} alt={selectedCompareModel.name} className="w-full h-48 object-contain mb-4" />
                    <h4 className="font-['Barlow_Condensed'] text-xl font-bold text-center text-[#0E3D95]">{selectedCompareModel.name}</h4>
                    <p className="text-center text-slate-500 text-sm mb-2">{selectedCompareModel.series}</p>
                    <p className="text-center text-[#F88200] font-bold text-xl">Call or email for pricing</p>
                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Dimensions:</span>
                        <span className="font-semibold">{selectedCompareModel.dimensions}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Seats:</span>
                        <span className={`font-semibold ${selectedCompareModel.persons > product.persons ? 'text-green-600' : selectedCompareModel.persons < product.persons ? 'text-orange-500' : ''}`}>
                          {selectedCompareModel.persons} Adults
                          {selectedCompareModel.persons > product.persons && ' ↑'}
                          {selectedCompareModel.persons < product.persons && ' ↓'}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Jets:</span>
                        <span className={`font-bold ${selectedCompareModel.jets > product.jets ? 'text-green-600' : selectedCompareModel.jets < product.jets ? 'text-orange-500' : 'text-[#F88200]'}`}>
                          {selectedCompareModel.jets}
                          {selectedCompareModel.jets > product.jets && ' ↑'}
                          {selectedCompareModel.jets < product.jets && ' ↓'}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Seating:</span>
                        <span className="font-semibold">{selectedCompareModel.seatingLayout}</span>
                      </div>
                      <div className="flex justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-500">Electrical:</span>
                        <span className="font-semibold">{selectedCompareModel.electrical}</span>
                      </div>
                      {selectedCompareModel.waterCapacity && (
                        <div className="flex justify-between border-b border-slate-100 pb-2">
                          <span className="text-slate-500">Water Capacity:</span>
                          <span className="font-semibold">{selectedCompareModel.waterCapacity}</span>
                        </div>
                      )}
                      {selectedCompareModel.dryWeight && (
                        <div className="flex justify-between border-b border-slate-100 pb-2">
                          <span className="text-slate-500">Dry Weight:</span>
                          <span className="font-semibold">{selectedCompareModel.dryWeight}</span>
                        </div>
                      )}
                      {selectedCompareModel.filledWeight && (
                        <div className="flex justify-between border-b border-slate-100 pb-2">
                          <span className="text-slate-500">Filled Weight:</span>
                          <span className="font-semibold">{selectedCompareModel.filledWeight}</span>
                        </div>
                      )}
                      {selectedCompareModel.pumps && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Pumps:</span>
                          <span className="font-semibold">{selectedCompareModel.pumps}</span>
                        </div>
                      )}
                    </div>
                    <Link 
                      to={`/products/${selectedCompareModel.id}`} 
                      className="block mt-4 text-center bg-[#F88200] hover:bg-orange-600 py-3 text-white font-semibold transition-colors"
                    >
                      View {selectedCompareModel.name} <ArrowRight size={14} className="inline ml-1" />
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="bg-white p-8 text-center border border-dashed border-slate-300">
                  <GitCompare size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Select a model from the dropdown above to compare specifications</p>
                </div>
              )}
              
              {/* Quick Compare Grid - Show all models at a glance */}
              <div className="mt-8">
                <h4 className="font-semibold text-[#0E3D95] mb-4">Quick Overview - All {product.brand} Models</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#0E3D95] text-white">
                        <th className="px-4 py-2 text-left">Model</th>
                        <th className="px-4 py-2 text-center">Seats</th>
                        <th className="px-4 py-2 text-center">Jets</th>
                        <th className="px-4 py-2 text-center">Seating</th>
                        <th className="px-4 py-2 text-right">Price</th>
                        <th className="px-4 py-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Current model row */}
                      <tr className="bg-orange-50 border-l-4 border-[#F88200]">
                        <td className="px-4 py-3 font-semibold">{product.name} <span className="text-xs text-[#F88200]">(current)</span></td>
                        <td className="px-4 py-3 text-center">{product.persons}</td>
                        <td className="px-4 py-3 text-center font-bold text-[#F88200]">{product.jets}</td>
                        <td className="px-4 py-3 text-center">{product.seatingLayout}</td>
                        <td className="px-4 py-3 text-right font-semibold">Call or email for pricing</td>
                        <td className="px-4 py-3 text-center">-</td>
                      </tr>
                      {/* Other models */}
                      {comparableModels.slice(0, 8).map((model, idx) => (
                        <tr key={model.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-4 py-3">{model.name}</td>
                          <td className="px-4 py-3 text-center">{model.persons}</td>
                          <td className="px-4 py-3 text-center font-semibold">{model.jets}</td>
                          <td className="px-4 py-3 text-center">{model.seatingLayout}</td>
                          <td className="px-4 py-3 text-right">Call or email for pricing</td>
                          <td className="px-4 py-3 text-center">
                            <Link to={`/products/${model.id}`} className="text-[#F88200] hover:underline text-xs font-semibold">
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
          
        </div>
      </div>
    </>
  );
};

export default ProductDetailPage;
